import React from 'react';
import Toast from '../../components/organisms/toast';
import { IPatient } from '../../libs/patient';
import { AppKey } from '../../libs/apps';
import { ISchedule } from '../../libs/schedule';
import { BaseResponse, PostJSON } from '../../libs/request';
import { TriageCategory, TriageData, TriageUpdaterValue } from './types';
import { RequestAction } from '../../libs/hooks/useRequestJSON';
import { ShouldAddToPaginatedResult } from './utils';
import moment from 'moment-timezone';
import { GetCurrentTZ } from '../../libs/time';
import { getSuspensionDates, ITask } from '../../libs/tasks';
import { useApps } from '../apps';

export const TriageUpdaterContext = React.createContext(null);

export function useTriageUpdater(): TriageUpdaterValue {
  const dispatch = React.useContext(TriageUpdaterContext);
  const { getAppName } = useApps();

  /**
   * Update the data for a patient object
   *
   * @param patient the new patient data
   */
  const handleUpdatePatient = (patient: IPatient) => {
    const updateFn = (draft: TriageData) => {
      const current =
        (draft.patients[patient.id] as IPatient) || ({} as IPatient);
      draft.patients[patient.id] = {
        ...current,
        ...patient
      } as IPatient;
    };
    dispatch({
      type: RequestAction.UpdateViaFn,
      updateFn
    });
  };

  /**
   * Resolve a patient
   *
   * Rectify category counts among Danger/Warning/Success
   * Rectify associated alert status (mark as read)
   * Rectify associated alert count
   * Rectify triage entry category information (primary & secondary)
   *
   * @param patient the updated patient object
   */
  const handleResolvePatient = (patient: IPatient) => {
    const updateFn = (draft: TriageData) => {
      // Update the patient
      draft.patients[patient.id] = patient;
      // If the patient is not part of the triage entries, do nothing
      const entry = draft.triages[patient.id];
      if (!entry) {
        return;
      }
      // Remove from current danger or warning category
      if (draft.pages?.warning?.entityIds.includes(patient.id)) {
        draft.pages.warning.entityIds = draft.pages.warning.entityIds.filter(
          (id) => id !== patient.id
        );
        draft.pages.warning.total =
          draft.pages.warning.total === 0 ? 0 : draft.pages.warning.total - 1;
      } else if (draft.pages?.danger?.entityIds.includes(patient.id)) {
        draft.pages.danger.entityIds = draft.pages.danger.entityIds.filter(
          (id) => id !== patient.id
        );
        draft.pages.danger.total =
          draft.pages.danger.total === 0 ? 0 : draft.pages.danger.total - 1;
      }
      // Add to success category if appropriate
      if (
        draft.pages?.success &&
        !draft.pages.success.entityIds.includes(patient.id)
      ) {
        if (ShouldAddToPaginatedResult(draft.pages.success)) {
          draft.pages.success.entityIds.push(patient.id);
        }
        draft.pages.success.total = draft.pages.success.total + 1;
      }
      // Resolve the alerts as well
      for (const alertId of Object.keys(draft.alerts)) {
        const alert = draft.alerts[alertId];
        if (alert.patientId === patient.id && !alert.isRead) {
          alert.isRead = true;
          alert.readAt = new Date();
          draft.alerts[alertId] = alert;
        }
      }
      // Update triage entry primaryCategoryId / secondaryCategoryIds
      if (
        entry.primaryCategoryId === TriageCategory.Danger ||
        entry.primaryCategoryId === TriageCategory.Warning
      ) {
        entry.primaryCategoryId = TriageCategory.Success;
      }
      // Remove snooze status once resolved
      if (entry.secondaryCategoryIds.includes(TriageCategory.Snoozed)) {
        entry.secondaryCategoryIds = entry.secondaryCategoryIds.filter(
          (category) => category !== TriageCategory.Snoozed
        );
      }
      // Update the app information
      for (const appKey of Object.keys(entry.apps)) {
        // Update alerts
        entry.apps[appKey].alert.total = 0;
        entry.apps[appKey].alert.unread = 0;
        entry.apps[appKey].alert.active = 0;
        // Update category
        if (
          entry.apps[appKey].category === TriageCategory.Danger ||
          entry.apps[appKey].category === TriageCategory.Warning
        ) {
          entry.apps[appKey].category = TriageCategory.Success;
        }
      }
      // Save the entry back to the draft
      draft.triages[patient.id] = entry;
    };
    dispatch({
      type: RequestAction.UpdateViaFn,
      updateFn
    });
    // Notify the user
    Toast({
      intent: 'success',
      message: `${patient.firstName} was resolved successfully!`
    });
  };

  /**
   * Handle the change of the archival status
   *
   * @param patient the patient of interest
   */
  const handleToggleArchivePatient = (patient: IPatient) => {
    const patientId = patient.id;
    const isArchived = patient.isArchived;
    // When it's archived, remove the patient entry from the context
    if (isArchived) {
      const updateFn = (draft: TriageData) => {
        // Remove the triage entry
        for (const key of Object.keys(draft.pages)) {
          if (key === 'archive') {
            continue;
          }
          if (
            draft.pages[key] &&
            draft.pages[key].entityIds.includes(patientId)
          ) {
            draft.pages[key].entityIds = draft.pages[key].entityIds.filter(
              (id: number) => id !== patientId
            );
          }
        }
        // Update the patient
        draft.patients[patientId] = patient;
        // TODO: properly rectify alerts associated
      };
      dispatch({
        type: RequestAction.UpdateViaFn,
        updateFn
      });
      // Notify the user
      Toast({
        intent: 'success',
        message: `${patient.firstName} has been archived!`
      });
      // Otherwise, re-add the patient entry
    } else {
      const updateFn = (draft: TriageData) => {
        // Update the patient
        draft.patients[patientId] = patient;
      };
      dispatch({
        type: RequestAction.UpdateViaFn,
        updateFn
      });
      // TODO: Do a re-fetch of the triage entry for the patient
      // Notify the user
      Toast({
        intent: 'success',
        message: `${patient.firstName} has been unarchived!`
      });
    }
  };

  /**
   * Handle the change of the pause (suspension) status
   *
   * @param patient the patient of interest
   */
  const handleTogglePausePatient = (
    patient: IPatient,
    cancelSuspension?: boolean,
    tasks?: ITask[],
    pausedApps?: string[]
  ) => {
    const tz = GetCurrentTZ();
    const patientId = patient.id;
    const isPaused = patient.isSuspended;
    let message = '';
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (tasks) {
      const suspensionDates = getSuspensionDates(tasks);
      if (suspensionDates) {
        startDate = suspensionDates.startDate || null;
        endDate = suspensionDates.endDate || null;
      }
    }

    const hasProgramSpecificPause = pausedApps && pausedApps.length > 0;

    if (isPaused) {
      // Patient is currently paused.
      if (hasProgramSpecificPause) {
        const appNames = pausedApps
          .map((key) => (getAppName && getAppName(key)) || key)
          .join(', ');
        if (endDate) {
          message = `${
            patient.firstName
          }'s ${appNames} program(s) paused until ${moment
            .tz(endDate, tz)
            .format('YYYY-MM-DD')}.`;
        } else {
          message = `${patient.firstName}'s ${appNames} program(s) are currently paused.`;
        }
      } else if (endDate) {
        message = `${
          patient.firstName
        }'s service was paused immediately and will resume at the start of ${moment
          .tz(endDate, tz)
          .format('YYYY-MM-DD')}.`;
      } else {
        message = `${patient.firstName}'s service is currently paused.`;
      }
    } else if (startDate && endDate) {
      // Patient was scheduled for a pause.
      if (hasProgramSpecificPause) {
        const appNames = pausedApps
          .map((key) => (getAppName && getAppName(key)) || key)
          .join(', ');
        message = `${patient.firstName}'s ${appNames} program(s) scheduled to pause on ${moment
          .tz(startDate, tz)
          .format('YYYY-MM-DD')} until ${moment
          .tz(endDate, tz)
          .format('YYYY-MM-DD')}.`;
      } else {
        message = `${
          patient.firstName
        }'s service is scheduled to be paused on ${moment
          .tz(startDate, tz)
          .format('YYYY-MM-DD')} and will resume at the start of ${moment
          .tz(endDate, tz)
          .format('YYYY-MM-DD')}.`;
      }
    } else {
      // Handle cancel and resume
      if (cancelSuspension) {
        message = `${patient.firstName}'s service pause has been cancelled succesfully.`;
      } else {
        message = `${patient.firstName}'s service has resumed successfully.`;
      }
    }
    if (isPaused) {
      const updateFn = (draft: TriageData) => {
        // Add to category list
        if (draft.pages?.paused) {
          if (!draft.pages.paused.entityIds.includes(patientId)) {
            if (ShouldAddToPaginatedResult(draft.pages.paused)) {
              draft.pages.paused.entityIds.push(patientId);
            }
          }
          draft.pages.paused.total = draft.pages.paused.total + 1;
        }
        // Update the patient
        draft.patients[patientId] = patient;
        draft.patients[patientId].tasks = tasks;
      };
      dispatch({
        type: RequestAction.UpdateViaFn,
        updateFn
      });
    } else {
      const updateFn = (draft: TriageData) => {
        // Remove from category list
        if (draft.pages?.paused) {
          if (draft.pages.paused.entityIds.includes(patientId)) {
            draft.pages.paused.entityIds = draft.pages.paused.entityIds.filter(
              (id) => id !== patientId
            );
          }
          draft.pages.paused.total = draft.pages.paused.total - 1;
        }
        // Update the patient
        draft.patients[patientId] = patient;
        draft.patients[patientId].tasks = tasks;
      };
      dispatch({
        type: RequestAction.UpdateViaFn,
        updateFn
      });
    }
    // Notify the user
    Toast({
      intent: 'success',
      message
    });
    return;
  };

  /**
   * Handle the update of a schedule (including cancelation)
   *
   * @param patient the patient of interest
   * @param key the app key of the schedule
   * @param schedule the new schedule
   */
  const handleUpdateSchedule = (
    patient: IPatient,
    key: AppKey,
    schedule: ISchedule
  ) => {
    const patientId = patient.id;
    // Locate the entry and update the schedule object
    const updateFn = (draft: TriageData) => {
      const entry = draft.triages[patientId];
      if (entry && entry.apps[key]) {
        draft.triages[patientId].apps[key].schedule = schedule;
        /**
         * There are edit prescription instances where a user can
         * select a contact that currently isn't in the triageData
         * contacts object. This causes unexpected behavior.
         * The below code prevents this behavior.
         */
        if (schedule && schedule.contact) {
          if (!draft.contacts[schedule.contactId]) {
            draft.contacts[schedule.contactId] = schedule.contact;
          }
        }
      }
    };
    dispatch({
      type: RequestAction.UpdateViaFn,
      updateFn
    });
  };

  /**
   * Handle the mark as seen for a patient
   *
   * @param patient the patient that has been marked as seen
   */
  const handleMarkSeen = (patient: IPatient) => {
    const patientId = patient.id;
    PostJSON<BaseResponse>({
      url: '/patient/mark_recent_seen/',
      params: {
        id: patientId
      }
    }).then((response) => {
      if (response.status !== 'OK') {
        // Notify the user
        Toast({
          intent: 'danger',
          message: `${patient.firstName} could not be marked as seen! Please try again.`
        });
      } else {
        // Remove it from the recent category
        const updateFn = (draft: TriageData) => {
          // Remove from category list
          if (draft.pages?.recent) {
            if (draft.pages.recent.entityIds.includes(patientId)) {
              draft.pages.recent.entityIds =
                draft.pages.recent.entityIds.filter((id) => id !== patientId);
            }
            draft.pages.recent.total = draft.pages.recent.total - 1;
          }
        };
        // Update Triage Data
        dispatch({
          type: RequestAction.UpdateViaFn,
          updateFn
        });
        // Notify the user
        Toast({
          intent: 'success',
          message: `${patient.firstName} has been marked as seen!`
        });
      }
      return;
    });
  };

  // Push message
  const handlePushMessage = () => {
    // TODO: push message implementation
  };

  return {
    handleUpdatePatient,
    handleResolvePatient,
    handleToggleArchivePatient,
    handleTogglePausePatient,
    handleUpdateSchedule,
    handleMarkSeen,
    handlePushMessage
  };
}
